import { useEffect } from 'react';

import { logUnknownErrorResponse } from '@hungryroot/o11y';

import {
  DEFAULT_QUIZ_STORAGE_NAMESPACE,
  type QuizStorageNamespace,
} from '../defs/localStorage';
import { type QuizPreferenceItem } from '../segment/quizAnalytics';

export function getSelectionImageStorageKeys(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  return {
    imageSelections: `${namespace}.image_selections`,
    loadingScreenImages: `${namespace}.loading_screen_images`,
  };
}

export const LOADING_SCREEN_CACHE_KEY =
  getSelectionImageStorageKeys().loadingScreenImages;

// Utilities for storing and retrieving images of foods that users have selected in the quiz,
// used for the AI loading screen.

export type SelectionImageCategory =
  | 'breakfast'
  | 'lunch'
  | 'dinner'
  | 'snacks'
  | 'sweets';

interface SelectionImage {
  id: number;
  name: string;
  imageUrl: string;
}

interface StoredSelectionImages {
  [mealType: string]: SelectionImage[];
}

function getStoredImages(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): StoredSelectionImages {
  try {
    const stored = sessionStorage.getItem(
      getSelectionImageStorageKeys(namespace).imageSelections
    );
    if (stored) return JSON.parse(stored);
  } catch (error) {
    logUnknownErrorResponse(
      'Failed to read selection images from sessionStorage',
      {
        key: 'loadingScreen.selectionImages.read.error',
        error: error instanceof Error ? error.message : String(error),
      }
    );
  }
  return {};
}

function saveImages(
  images: StoredSelectionImages,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  try {
    sessionStorage.setItem(
      getSelectionImageStorageKeys(namespace).imageSelections,
      JSON.stringify(images)
    );
  } catch (error) {
    logUnknownErrorResponse(
      'Failed to save selection images to sessionStorage',
      {
        key: 'loadingScreen.selectionImages.write.error',
        error: error instanceof Error ? error.message : String(error),
      }
    );
  }
}

function invalidateLoadingScreenCache(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  try {
    sessionStorage.removeItem(
      getSelectionImageStorageKeys(namespace).loadingScreenImages
    );
  } catch (error) {
    logUnknownErrorResponse(
      'Failed to invalidate loading screen cache in sessionStorage',
      {
        key: 'loadingScreen.cache.invalidate.error',
        error: error instanceof Error ? error.message : String(error),
      }
    );
  }
}

function saveSelectionImagesAndInvalidateCache(
  mealType: SelectionImageCategory,
  items: SelectionImage[],
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  const images = getStoredImages(namespace);
  images[mealType] = items;
  saveImages(images, namespace);
  invalidateLoadingScreenCache(namespace);
}

export function storeSelectionImages(
  mealType: SelectionImageCategory,
  items: QuizPreferenceItem[],
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  const selectionImages = items
    .filter((item) => item.imageUrl)
    .map((item) => ({
      id: item.id,
      name: item.name,
      imageUrl: item.imageUrl as string,
    }));

  saveSelectionImagesAndInvalidateCache(mealType, selectionImages, namespace);
}

export function getSelectionImages(
  mealType?: SelectionImageCategory,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): SelectionImage[] {
  const images = getStoredImages(namespace);

  if (mealType) return images[mealType] ?? [];

  return Object.values(images).flat();
}

export function clearSelectionImages(
  mealType?: SelectionImageCategory,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  if (mealType) {
    const images = getStoredImages(namespace);
    delete images[mealType];
    saveImages(images, namespace);
    return;
  }

  try {
    sessionStorage.removeItem(
      getSelectionImageStorageKeys(namespace).imageSelections
    );
  } catch (error) {
    logUnknownErrorResponse(
      'Failed to clear selection images from sessionStorage',
      {
        key: 'loadingScreen.selectionImages.clear.error',
        error: error instanceof Error ? error.message : String(error),
      }
    );
  }
}

export function upsertSelectionImage(
  mealType: SelectionImageCategory,
  image: SelectionImage,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  const images = getStoredImages(namespace);
  const existing = images[mealType] ?? [];
  images[mealType] = [
    ...existing.filter((item) => item.id !== image.id),
    image,
  ];
  saveImages(images, namespace);
  invalidateLoadingScreenCache(namespace);
}

export function removeSelectionImage(
  mealType: SelectionImageCategory,
  id: number,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  const images = getStoredImages(namespace);
  const existing = images[mealType] ?? [];
  images[mealType] = existing.filter((item) => item.id !== id);
  saveImages(images, namespace);
  invalidateLoadingScreenCache(namespace);
}

interface SyncableItem {
  id: number;
  name: string;
  featured_img_url?: string | null;
  card_image_url?: string | null;
}

interface UseSyncInitialSelectionImagesParams {
  mealType: SelectionImageCategory | undefined;
  selectedIds: number[] | null | undefined;
  items: SyncableItem[] | undefined;
  enabled: boolean;
  namespace?: QuizStorageNamespace;
}

/**
 * This ensures that existing selections from the API are captured,
 * not just user interactions.
 */
export function useSyncInitialSelectionImages({
  mealType,
  selectedIds,
  items,
  enabled,
  namespace = DEFAULT_QUIZ_STORAGE_NAMESPACE,
}: UseSyncInitialSelectionImagesParams): void {
  useEffect(() => {
    if (
      !mealType ||
      !enabled ||
      !items ||
      !selectedIds ||
      selectedIds.length === 0
    )
      return;

    const selectedItems: SelectionImage[] = items
      .filter((item) => selectedIds.includes(item.id))
      .filter((item) => item.featured_img_url || item.card_image_url)
      .map((item) => ({
        id: item.id,
        name: item.name,
        imageUrl: (item.featured_img_url || item.card_image_url) as string,
      }));

    if (selectedItems.length > 0) {
      saveSelectionImagesAndInvalidateCache(mealType, selectedItems, namespace);
    }
  }, [mealType, selectedIds, items, enabled, namespace]);
}
